import { createFileRoute } from '@tanstack/react-router'
import { FeaturePage } from '@/components/feature-page'
import { ProductFrame } from '@/components/marketing/product-frame'
import { UptimeBarsPiece } from '@/components/marketing/ui-pieces'
import { breadcrumbList, jsonLdScript, webPage } from '@/lib/schema'

export const Route = createFileRoute('/_site/features/uptime-monitoring')({
  component: UptimeMonitoringPage,
  head: () => ({
    meta: [
      { title: 'Cheapest 30-Second Uptime Monitoring | Uptrack' },
      { name: 'description', content: 'The cheapest 30-second uptime monitoring: 30s HTTP/HTTPS checks, instant alerts, response-time tracking. From $3/mo.' },
      { property: 'og:title', content: 'Cheapest 30-Second Uptime Monitoring | Uptrack' },
      { property: 'og:description', content: 'The cheapest 30-second uptime monitoring. 30-second checks, instant alerts, response time tracking. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/features-uptime-monitoring.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/features/uptime-monitoring' },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Features', url: 'https://uptrack.app/features' },
        { name: 'Uptime Monitoring', url: 'https://uptrack.app/features/uptime-monitoring' },
      ])),
      jsonLdScript(webPage({
        name: 'Uptrack Uptime Monitoring',
        description: 'HTTP/HTTPS website uptime monitoring with 30-second check intervals and instant downtime alerts.',
        url: 'https://uptrack.app/features/uptime-monitoring',
      })),
    ],
  }),
})

function UptimeMonitoringPage() {
  return (
    <FeaturePage
      section="HTTP/HTTPS uptime monitoring"
      title="The cheapest uptime monitoring every 30 seconds."
      intro="Monitor any HTTP or HTTPS endpoint. Detect downtime in under a minute, confirm outages before alerting, and track response times over time. From $3/mo — cheaper than UptimeRobot, Pingdom, and Better Stack for 30s checks."
      featuresHeading="How Uptrack monitors your sites"
      features={[
        { title: '30-second checks', desc: 'Every plan runs monitors at 30-second intervals. Detect downtime 10x faster than 5-minute competitors.' },
        { title: 'Alert confirmation', desc: 'Consecutive failed checks confirm an outage before you get alerted. No more false positives from transient blips.' },
        { title: 'Response time tracking', desc: 'Track latency over time. Spot degradation trends before they become full outages.' },
        { title: 'Capacity that scales', desc: 'Pro starts with 50 monitors, with higher limits on Team and Business.' },
      ]}
      table={{
        heading: 'Uptrack vs the alternatives',
        intro: 'Most competitors lock fast check intervals behind paid plans at $10+/mo. Uptrack checks every 30 seconds on its free plan too, and Pro is $3/mo for 50 monitors.',
        columns: ['Provider', 'Cheapest-plan interval', 'Alert Confirm', 'Response Time'],
        rows: [
          { cells: ['UptimeRobot', '5 min (free plan)', 'Paid', 'Yes'] },
          { cells: ['Better Stack', '3 min (free plan)', 'Yes', 'Yes'] },
          { cells: ['Pingdom', '1 min', 'Yes', 'Yes'] },
          { cells: ['Uptrack', '30s on every plan, free included', 'Yes', 'Yes'], highlight: true },
        ],
      }}
      why={{ heading: 'Why uptime monitoring matters', body: 'Every minute of undetected downtime costs revenue and trust. A 5-minute check interval means up to 4 minutes and 59 seconds of invisible downtime. With 30-second checks, you know in under a minute.' }}
      cta={{ heading: 'Start monitoring your sites now', body: 'Every plan includes 30-second checks and alert confirmation.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard">
          <UptimeBarsPiece />
        </ProductFrame>
      }
    />
  )
}
